// ─────────────────────────────────────────────────────────────────────────────
// graphUiState — the dock's preferences, on this machine only.
//
// LOCAL, VALIDATED, AND NEVER TRUSTED. Everything here came out of localStorage,
// which is to say out of an earlier version of this code, a hand edit, or a
// different build entirely. Every field is checked and anything unrecognised is
// replaced by the default rather than fed to the renderer — a persisted
// `nodeScale: "big"` must not reach a uniform.
//
// It is a VIEW preference store. No graph data, no selection, no camera, and
// nothing goes near the backend: what the owner is looking at is not a fact
// about Rexy.
// ─────────────────────────────────────────────────────────────────────────────

import { GraphLabelMode } from './graphLabels';

export type GraphDockTab = 'structure' | 'search' | 'filters' | 'display' | 'inspector' | 'dev';

const TABS: GraphDockTab[] = ['structure', 'search', 'filters', 'display', 'inspector', 'dev'];
const LABEL_MODES: GraphLabelMode[] = ['selection', 'context', 'all'];

export const GRAPH_UI_STORAGE_KEY = 'rexy.spatial-graph.ui.v1';

export interface GraphUiState {
  open: boolean;
  activeTab: GraphDockTab;
  labelMode: GraphLabelMode;
  nodeScale: number;
  linkOpacity: number;
  sparkOpacity: number;
  /**
   * OFF BY DEFAULT (G1.2 ruling). The isolated render measured 258,000 spark
   * points producing zero lit pixels — pure build, memory and draw cost for no
   * visible contribution. The architecture stays; only the default spend goes.
   */
  edgeSparksEnabled: boolean;
  /** Filter maps are stored as-is; unknown keys are dropped on load. */
  domains: { [slug: string]: boolean };
  kinds: { [group: string]: boolean };
  trust: { [tier: string]: boolean };
  relations: { [kind: string]: boolean };
}

export const DEFAULT_UI_STATE: GraphUiState = {
  // COLLAPSED BY DEFAULT. The graph is the object on screen; the controls are not.
  open: false,
  activeTab: 'search',
  labelMode: 'selection',
  nodeScale: 1,
  linkOpacity: 1,
  sparkOpacity: 1,
  edgeSparksEnabled: false,
  domains: {},
  kinds: {},
  trust: {},
  relations: {},
};

function boolMap(value: unknown): { [k: string]: boolean } {
  const out: { [k: string]: boolean } = {};
  if (!value || typeof value !== 'object') return out;
  const src = value as { [k: string]: unknown };
  const keys = Object.keys(src);
  for (let i = 0; i < keys.length; i++) {
    if (typeof src[keys[i]] === 'boolean') out[keys[i]] = src[keys[i]] as boolean;
  }
  return out;
}

function num(value: unknown, fallback: number, lo: number, hi: number): number {
  if (typeof value !== 'number' || !isFinite(value)) return fallback;
  return Math.min(hi, Math.max(lo, value));
}

export function loadUiState(): GraphUiState {
  try {
    const raw = window.localStorage.getItem(GRAPH_UI_STORAGE_KEY);
    if (!raw) return DEFAULT_UI_STATE;
    const parsed = JSON.parse(raw) as { [k: string]: unknown };
    return {
      open: typeof parsed.open === 'boolean' ? parsed.open : DEFAULT_UI_STATE.open,
      activeTab:
        TABS.indexOf(parsed.activeTab as GraphDockTab) >= 0
          ? (parsed.activeTab as GraphDockTab)
          : DEFAULT_UI_STATE.activeTab,
      labelMode:
        LABEL_MODES.indexOf(parsed.labelMode as GraphLabelMode) >= 0
          ? (parsed.labelMode as GraphLabelMode)
          : DEFAULT_UI_STATE.labelMode,
      nodeScale: num(parsed.nodeScale, 1, 0.5, 2.5),
      linkOpacity: num(parsed.linkOpacity, 1, 0, 4),
      sparkOpacity: num(parsed.sparkOpacity, 1, 0, 3),
      edgeSparksEnabled:
        typeof parsed.edgeSparksEnabled === 'boolean' ? parsed.edgeSparksEnabled : false,
      domains: boolMap(parsed.domains),
      kinds: boolMap(parsed.kinds),
      trust: boolMap(parsed.trust),
      relations: boolMap(parsed.relations),
    };
  } catch (err) {
    // Unreadable or unparseable storage is a defaults case, never a crash.
    return DEFAULT_UI_STATE;
  }
}

let saveTimer: number | null = null;

/**
 * Coalesced write. Dragging a slider produces a write per pixel otherwise, and
 * localStorage is synchronous — that is a frame cost for a preference nobody
 * needs persisted mid-gesture.
 */
export function saveUiState(state: GraphUiState): void {
  if (saveTimer !== null) window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    saveTimer = null;
    try {
      window.localStorage.setItem(GRAPH_UI_STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      // Private mode, quota, disabled storage: losing a preference is not an error
      // worth interrupting the session for.
    }
  }, 350);
}
